<div class="profile-container mat-elevation-z4">
  <ng-container *ngIf="!dataLoading; else skeletonLoader">
    <!-- Banner/Header -->
    <div class="header">
      <img
        class="cover-photo"
        [src]="
          model?.profile_banner_url ||
          'assets/images/placeholder_banner_image.png'
        "
        alt="Cover Photo"
      />
    </div>
    <!-- Unified Profile Header -->
    <div class="profile-header">
      <img
        class="avatar"
        [src]="
          model?.profile_image_url
            ? model?.profile_image_url
            : model?.gender === 'Male'
            ? 'assets/images/placeholder_male_profile_image.png'
            : 'assets/images/placeholder_profile_image.png'
        "
        alt="Profile Picture"
      />
      <div class="identity">
        <h2>{{ model?.name }} | {{ model?.age }}</h2>
        <p class="sub">{{ model?.gender }}</p>
        <p class="sub">{{ model?.city }}, {{ model?.province }}</p>
        <div class="stats-row" *ngIf="model">
          <div class="stat">
            <mat-icon
              [fontIcon]="(isLiked$ | async) ? 'favorite' : 'favorite_border'"
              class="stat-icon like-icon"
              [ngClass]="{ liked: (isLiked$ | async) }"
              (click)="toggleLike()"
            ></mat-icon>
            <span>{{ model.likesCount }}</span>
          </div>
          <div class="stat">
            <mat-icon fontIcon="visibility" class="stat-icon"></mat-icon>
            <span>{{ model.viewsCount ?? 0 }}</span>
          </div>
          <div class="stat">
            <mat-icon fontIcon="image" class="stat-icon"></mat-icon>
            <span>{{ model.imagesCount ?? '—' }}</span>
          </div>
          <div class="stat">
            <mat-icon fontIcon="videocam" class="stat-icon"></mat-icon>
            <span>{{ model.videosCount ?? '—' }}</span>
          </div>
        </div>
        <div class="social-row">
          <a
            *ngIf="model?.phone_number"
            [href]="isMobile ? 'tel:' + model?.phone_number : null"
            (click)="onPhoneClick($event)"
            aria-label="Call"
          >
            <img src="assets/icons/phone.png" alt="Call" />
          </a>
          <a
            *ngIf="model?.instagram_url"
            [href]="model?.instagram_url"
            target="_blank"
          >
            <img src="assets/icons/instagram.png" alt="Instagram" />
          </a>
          <a
            *ngIf="model?.twitter_url"
            [href]="model?.twitter_url"
            target="_blank"
          >
            <img src="assets/icons/twitter.png" alt="Twitter" />
          </a>
        </div>
      </div>
    </div>

    <!-- Profile Complete Banner - Shows only when completed but not acknowledged -->
    <mat-card *ngIf="isOwnProfile && userOnboardingLoaded && showOnboardingBanner" class="onboarding-banner success-banner">
      <mat-card-content class="banner-content">
        <mat-icon class="banner-icon">celebration</mat-icon>
        <div class="banner-text">
          <h3>Profile Complete!</h3>
          <p>Your profile is looking great. You're all set to connect with clients!</p>
        </div>
        <button mat-icon-button (click)="acknowledgeOnboardingBanner()" aria-label="Dismiss">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card-content>
    </mat-card>

    <!-- Onboarding Checklist - Waits for user data before deciding visibility -->
    <app-model-onboarding-checklist 
      *ngIf="isOwnProfile && model && userOnboardingLoaded && !isModelOnboardingComplete"
      [modelId]="model.model_id"
    ></app-model-onboarding-checklist>

    <!-- Description -->
    <div class="model-description">
      <p>{{ model?.description }}</p>
    </div>
    <!-- Tags -->
    <div *ngIf="tags.length > 1" class="tags-container">
      <span *ngFor="let tag of displayedTags" class="tag-pill">{{ tag }}</span>
      <button (click)="toggleTags()">
        {{ showAllTags ? "Show Less" : "View All" }}
      </button>
    </div>
    <!-- Tabs / Media -->
    <mat-tab-group class="tab-group">
      <mat-tab label="Images">
        <div *ngIf="images.length > 0; else noImages" class="gallery">
          <div class="media-card" *ngFor="let image of images; let i = index">
            <img
              [src]="image.firebaseUrl"
              alt="Gallery Image"
              (load)="onImageLoad(i)"
              (error)="onImageError(i)"
              [style.visibility]="imageLoading[i] ? 'hidden' : 'visible'"
            />
            <div class="media-overlay">
              <div class="ov-stat">
                <mat-icon
                  [fontIcon]="
                    (imageLikedStates[image.id] | async)
                      ? 'favorite'
                      : 'favorite_border'
                  "
                  class="ov-icon"
                  [ngClass]="{ liked: (imageLikedStates[image.id] | async) }"
                  (click)="toggleImageLike(image)"
                ></mat-icon>
                <span>{{ image.likesCount }}</span>
              </div>
              <div class="ov-stat">
                <mat-icon fontIcon="visibility" class="ov-icon"></mat-icon>
                <span>{{ image.viewsCount }}</span>
              </div>
              <mat-icon
                fontIcon="fullscreen"
                class="fullscreen-btn"
                (click)="openFullscreen(i)"
              ></mat-icon>
            </div>
            <div *ngIf="imageLoading[i]" class="spinner-overlay">
              <mat-spinner
                diameter="40"
                aria-label="Loading image"
              ></mat-spinner>
            </div>
          </div>
        </div>
        <ng-template #noImages>
          <p class="no-content-message">
            No images yet — finding a better angle 📸
          </p>
        </ng-template>
      </mat-tab>
      <mat-tab label="Videos">
        <div *ngIf="videos.length > 0; else noVideos" class="gallery">
          <div class="media-card" *ngFor="let video of videos; let i = index">
            <video
              #vidRef
              [attr.src]="video.firebaseUrl"
              preload="metadata"
              muted
              playsinline
              (loadeddata)="onVideoLoad(i)"
              (play)="videoPlaying[i] = true"
              (pause)="videoPlaying[i] = false"
              (ended)="videoPlaying[i] = false"
              (error)="onVideoError(i)"
              [style.visibility]="videoLoading[i] ? 'hidden' : 'visible'"
            ></video>
            <div class="media-overlay">
              <div class="ov-stat">
                <mat-icon
                  [fontIcon]="videoPlaying[i] ? 'pause' : 'play_arrow'"
                  class="ov-icon"
                  (click)="toggleVideoPlayback(vidRef, video, i)"
                ></mat-icon>
              </div>
              <div class="ov-stat">
                <mat-icon
                  fontIcon="fullscreen"
                  class="ov-icon"
                  (click)="enterVideoFullscreen(vidRef)"
                ></mat-icon>
              </div>
              <div class="ov-stat">
                <mat-icon
                  [fontIcon]="
                    (videoLikedStates[video.id] | async)
                      ? 'favorite'
                      : 'favorite_border'
                  "
                  class="ov-icon"
                  [ngClass]="{ liked: (videoLikedStates[video.id] | async) }"
                  (click)="toggleVideoLike(video)"
                ></mat-icon>
                <span>{{ video.likesCount }}</span>
              </div>
              <div class="ov-stat">
                <mat-icon fontIcon="visibility" class="ov-icon"></mat-icon>
                <span>{{ video.viewsCount }}</span>
              </div>
            </div>
            <div *ngIf="videoLoading[i]" class="spinner-overlay">
              <mat-spinner
                diameter="40"
                aria-label="Loading video"
              ></mat-spinner>
            </div>
          </div>
        </div>
        <ng-template #noVideos>
          <p class="no-content-message">No videos yet — stay tuned 🎥</p>
        </ng-template>
      </mat-tab>
    </mat-tab-group>
  </ng-container>
  <ng-template #skeletonLoader>
    <div class="header">
      <app-skeleton
        [width]="1000"
        [height]="220"
        [borderRadius]="0"
      ></app-skeleton>
    </div>
    <div class="profile-header" style="padding-top: 70px">
      <app-skeleton
        [width]="130"
        [height]="130"
        [borderRadius]="65"
        style="position: absolute; top: -65px; left: 24px"
      ></app-skeleton>
      <div class="identity" style="margin-left: 170px; width: 100%">
        <app-skeleton
          [width]="260"
          [height]="32"
          [borderRadius]="6"
          style="margin-bottom: 12px"
        ></app-skeleton>
        <app-skeleton
          [width]="180"
          [height]="20"
          [borderRadius]="6"
          style="margin-bottom: 8px"
        ></app-skeleton>
        <app-skeleton
          [width]="200"
          [height]="20"
          [borderRadius]="6"
          style="margin-bottom: 12px"
        ></app-skeleton>
        <div style="display: flex; gap: 18px; margin-top: 12px">
          <app-skeleton
            *ngFor="let i of [1, 2, 3, 4]"
            [width]="60"
            [height]="20"
            [borderRadius]="10"
          ></app-skeleton>
        </div>
        <div style="display: flex; gap: 14px; margin-top: 14px">
          <app-skeleton
            *ngFor="let i of [1, 2]"
            [width]="32"
            [height]="32"
            [borderRadius]="16"
          ></app-skeleton>
        </div>
      </div>
    </div>
    <div class="model-description">
      <app-skeleton
        [width]="700"
        [height]="24"
        [borderRadius]="6"
        style="margin-bottom: 8px"
      ></app-skeleton>
      <app-skeleton
        [width]="600"
        [height]="20"
        [borderRadius]="6"
      ></app-skeleton>
    </div>
    <div
      class="tags-container"
      style="justify-content: center; padding-bottom: 10px"
    >
      <app-skeleton
        *ngFor="let i of [1, 2, 3]"
        [width]="80"
        [height]="28"
        [borderRadius]="14"
      ></app-skeleton>
    </div>
    <mat-tab-group class="tab-group">
      <mat-tab label="Images">
        <div class="gallery">
          <app-skeleton
            *ngFor="let i of [1, 2, 3]"
            [width]="180"
            [height]="120"
            [borderRadius]="10"
          ></app-skeleton>
        </div>
      </mat-tab>
      <mat-tab label="Videos">
        <div class="gallery">
          <app-skeleton
            *ngFor="let i of [1, 2]"
            [width]="180"
            [height]="120"
            [borderRadius]="10"
          ></app-skeleton>
        </div>
      </mat-tab>
    </mat-tab-group>
  </ng-template>
</div>
